import React, { createContext, useContext, useState, useCallback } from 'react';

// Context for budget synchronization across the app
const BudgetSyncContext = createContext();

export const useBudgetSync = () => {
  const context = useContext(BudgetSyncContext);
  if (!context) {
    throw new Error('useBudgetSync must be used within a BudgetSyncProvider');
  }
  return context;
};

export const BudgetSyncProvider = ({ children }) => {
  // Track the last update timestamp for each event's budget
  const [budgetUpdates, setBudgetUpdates] = useState({});
  
  // Listeners for budget changes
  const [listeners, setListeners] = useState([]);

  // Call this when a budget is updated anywhere in the app
  const notifyBudgetUpdate = useCallback((eventId, budgetData = null) => {
    const timestamp = Date.now();
    
    setBudgetUpdates(prev => ({
      ...prev,
      [eventId]: { timestamp, data: budgetData }
    }));

    // Dispatch a custom event for components that prefer event-based updates
    window.dispatchEvent(new CustomEvent('budgetUpdated', { 
      detail: { eventId, timestamp, budgetData } 
    }));

    // Notify all registered listeners
    listeners.forEach(listener => {
      try {
        listener({ eventId, timestamp, budgetData });
      } catch (e) {
        console.error('Budget listener error:', e);
      }
    });
  }, [listeners]);

  // Check if a budget was recently updated (within the last 5 seconds)
  const wasBudgetRecentlyUpdated = useCallback((eventId) => {
    const update = budgetUpdates[eventId];
    if (!update) return false;
    return Date.now() - update.timestamp < 5000;
  }, [budgetUpdates]);

  // Get the last known budget data for an event
  const getLastBudgetUpdate = useCallback((eventId) => {
    return budgetUpdates[eventId] || null;
  }, [budgetUpdates]);

  // Register a listener for budget updates
  const addBudgetListener = useCallback((listener) => {
    setListeners(prev => [...prev, listener]);
    return () => {
      setListeners(prev => prev.filter(l => l !== listener));
    };
  }, []);

  // Trigger a global refresh for all budget displays
  const triggerGlobalBudgetRefresh = useCallback(() => {
    const timestamp = Date.now();
    window.dispatchEvent(new CustomEvent('budgetGlobalRefresh', { 
      detail: { timestamp } 
    }));
  }, []);

  const value = {
    notifyBudgetUpdate,
    wasBudgetRecentlyUpdated,
    getLastBudgetUpdate,
    addBudgetListener,
    triggerGlobalBudgetRefresh,
    budgetUpdates
  };

  return (
    <BudgetSyncContext.Provider value={value}>
      {children}
    </BudgetSyncContext.Provider>
  );
};

// Custom hook to subscribe to budget updates for a specific event
export const useBudgetUpdates = (eventId, onUpdate) => {
  React.useEffect(() => {
    const handleBudgetUpdate = (event) => {
      if (!eventId || event.detail.eventId === eventId) {
        onUpdate?.(event.detail);
      }
    };

    const handleGlobalRefresh = () => {
      onUpdate?.({ type: 'globalRefresh' });
    };

    window.addEventListener('budgetUpdated', handleBudgetUpdate);
    window.addEventListener('budgetGlobalRefresh', handleGlobalRefresh);

    return () => {
      window.removeEventListener('budgetUpdated', handleBudgetUpdate);
      window.removeEventListener('budgetGlobalRefresh', handleGlobalRefresh);
    };
  }, [eventId, onUpdate]);
};

// Hook to auto-refresh data when budget updates occur
export const useAutoRefreshOnBudgetUpdate = (refreshFn, dependencies = []) => {
  React.useEffect(() => {
    const handleBudgetUpdate = () => {
      refreshFn?.();
    };

    const handleGlobalRefresh = () => {
      refreshFn?.();
    };

    window.addEventListener('budgetUpdated', handleBudgetUpdate);
    window.addEventListener('budgetGlobalRefresh', handleGlobalRefresh);

    return () => {
      window.removeEventListener('budgetUpdated', handleBudgetUpdate);
      window.removeEventListener('budgetGlobalRefresh', handleGlobalRefresh);
    };
  }, [refreshFn, ...dependencies]);
};

export default BudgetSyncContext;
